// Cross-league ("EPL + La Liga") stats-view helpers. Scoring is identical
// across comps (D3 validation: EPL<->La Liga distributions align), so a
// merged table is a fair comparison. Home for anything a combined view
// needs so future surfaces (draft boards, player pools) reuse one
// implementation instead of hand-rolling a fourth copy of the doctrine.

// Sentinel "competition id" for the combined view (not a real DB comp).
export const COMBINED_COMP_ID = 'combined';

// Counting stats that sum when one player has rows in BOTH comps for the
// same season slot (January cross-league mover). FPPG recomputes from the
// summed totals; identity fields come from the majority-games row - the
// same games-weighted + majority-club doctrine the archive upsert and the
// draft-ranking generator use. Keep in lockstep with the backend season
// stat columns (PLAYER_COLUMNS keys cover the display subset).
export const SUMMED_STAT_FIELDS = [
  'fantasy_points', 'player_games_appeared', 'games_played', 'goals',
  'shots_on_target', 'shots', 'gca', 'sca', 'prog_passes', 'prog_carries',
  'dispossessed', 'corners_taken', 'free_kicks_taken', 'penalties_taken',
] as const;

export const mergeAndRerankCombined = (rows: any[]): any[] => {
  const byPlayer = new Map<number, any[]>();
  for (const r of rows) {
    const pid = Number(r.player_id ?? r.id);
    if (!Number.isFinite(pid)) continue;
    const bucket = byPlayer.get(pid);
    if (bucket) bucket.push(r); else byPlayer.set(pid, [r]);
  }
  const merged: any[] = [];
  for (const bucket of byPlayer.values()) {
    if (bucket.length === 1) { merged.push(bucket[0]); continue; }
    const games = (r: any) => Number(r.player_games_appeared ?? r.games_played ?? 0);
    const base = { ...bucket.reduce((a, b) => (games(b) > games(a) ? b : a)) };
    for (const field of SUMMED_STAT_FIELDS) {
      const vals = bucket.map((r) => r[field]).filter((v) => v != null && Number.isFinite(Number(v)));
      if (vals.length) base[field] = vals.reduce((s, v) => s + Number(v), 0);
    }
    const totalGames = Number(base.player_games_appeared ?? 0);
    base.fppg = totalGames > 0 ? Number((Number(base.fantasy_points ?? 0) / totalGames).toFixed(2)) : base.fppg;
    merged.push(base);
  }
  // Per-comp ranks are meaningless across leagues - recompute over the
  // merged pool so Rank/PosR read as combined ranks.
  merged.sort((a, b) => Number(b.fantasy_points ?? 0) - Number(a.fantasy_points ?? 0));
  const posCounts: Record<string, number> = {};
  const posPrevious: Record<string, { points: number; rank: number }> = {};
  let eligibleCount = 0;
  let previousOverall: { points: number; rank: number } | null = null;
  for (const r of merged) {
    const pos = String(r.primary_position ?? r.fantasy_position ?? r.fantasy_position_in_league ?? '');
    if (Number(r.player_games_appeared ?? 0) <= 0) {
      r.rank = null;
      r.position_rank = null;
      continue;
    }
    const points = Number(r.fantasy_points ?? 0);
    eligibleCount += 1;
    r.rank = previousOverall?.points === points ? previousOverall.rank : eligibleCount;
    previousOverall = { points, rank: r.rank };
    if (pos) {
      posCounts[pos] = (posCounts[pos] ?? 0) + 1;
      r.position_rank = posPrevious[pos]?.points === points ? posPrevious[pos].rank : posCounts[pos];
      posPrevious[pos] = { points, rank: r.position_rank };
    } else {
      r.position_rank = null;
    }
  }
  return merged;
};
